/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce from '@msdyn365-commerce/core';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IProductComparisonPreviewViewProps, ProductComparisonPreview } from './product-comparison-preview';

/**
 * Represents product comparison preview view.
 * @param props - View props for product comparison preview.
 * @returns Functional component of product comparison preview.
 */
export const ProductComparisonPreviewViewFunctionalComponent: React.FC<IProductComparisonPreviewViewProps> = props => {
    if (!props.isMainContentReady) {
        return null;
    }

    const shouldBeExpanded = props.isExpanded ?? props.hasUserInteractedWithComparison;

    const containerClassName = classnames(
        ProductComparisonPreview.mainContainerClassName,
        shouldBeExpanded ? '' : `${ProductComparisonPreview.mainContainerClassName}__collapsed`
    );

    return (
        <Module {...props.moduleProps}>
            <Node className={ProductComparisonPreview.containerClassName}>
                <Node className={containerClassName}>
                    <Node className={`${ProductComparisonPreview.mainContainerClassName}__header__section`}>
                        <Node {...props.collapseButton}>
                            <Node className={ProductComparisonPreview.headerClassName}>
                                <Node className={ProductComparisonPreview.headerLabelClassName}>
                                    <Node className={`${ProductComparisonPreview.headerLabelClassName}__icon`} />
                                    <Node className={`${ProductComparisonPreview.headerLabelClassName}__main-text`}>
                                        {props.resources.productComparisonPreviewHeaderText}
                                    </Node>
                                    <Node className={`${ProductComparisonPreview.headerLabelClassName}__sub-text`}>
                                        {props.subheaderText}
                                    </Node>
                                </Node>
                            </Node>
                        </Node>
                        <Node className={ProductComparisonPreview.tableClassName} tag='table'>
                            <Node className='' tag='tbody'>
                                <Node className={ProductComparisonPreview.tableRowClassName} tag='tr'>
                                    {props.productsMainInfo?.map(info => {
                                        return (
                                            <Node className='' tag='td' key={info.productData.itemId}>
                                                {info.removeFromComparisonButton}
                                                {info.productImage}
                                            </Node>
                                        );
                                    })}
                                    {props.previewGreyPlaceHolders}
                                </Node>
                                <Node className={ProductComparisonPreview.tableRowClassName} tag='tr'>
                                    {props.productsMainInfo?.map(info => {
                                        return (
                                            <Node className='' tag='td' key={info.productData.itemId}>
                                                {msdyn365Commerce.isBrowser ? info.productLink : undefined}
                                            </Node>
                                        );
                                    })}
                                </Node>
                            </Node>
                        </Node>
                    </Node>
                    <Node className={ProductComparisonPreview.footerClassName}>
                        {props.clearAllButton}
                        {props.goToComparisonButton}
                    </Node>
                </Node>
            </Node>
        </Module>
    );
};

export default ProductComparisonPreviewViewFunctionalComponent;
